/* ============================================
   PROTOBUF DECODER - Décodeur principal
   ============================================
   Décode l'enveloppe PushMsg { code, data } des
   messages MQTT. Le code est un varint complet
   (ex: 10101 = 0xF5 0x4E), pas un seul octet.

   Compatibilité ascendante : le champ `type`
   ("0xF5", "0xF6"...) est toujours présent pour
   que les anciens parsers continuent de fonctionner.
   Les nouveaux champs `code` et `data` permettent
   le décodage protobuf structuré.
   ============================================ */

/**
 * Mapping code PushMsg → type legacy pour compatibilité
 */
const CODE_TO_LEGACY_TYPE = {
  10101: '0xF5',
  10102: '0xF6',
  10103: '0xF7',
  10105: '0xF9',
  10106: '0xFA',
  10107: '0xFB',
};

/**
 * Décoder manuellement un PushMsg protobuf { code: int32, data: bytes }
 * Sans dépendance à protobufjs.
 * @param {Uint8Array|Buffer} buffer - Le buffer brut du message MQTT
 * @returns {{ code: number, data: Uint8Array }|null}
 */
function decodePushMsgManual(buffer) {
  let offset = 0;
  let code = null;
  let data = null;

  while (offset < buffer.length) {
    if (offset >= buffer.length) break;
    const tagByte = buffer[offset];
    const fieldNumber = tagByte >> 3;
    const wireType = tagByte & 0x07;
    offset++;

    if (fieldNumber === 1 && wireType === 0) {
      // Field 1 = code (varint)
      const v = readVarint(buffer, offset);
      code = v.value;
      offset += v.bytesRead;
    } else if (fieldNumber === 2 && wireType === 2) {
      // Field 2 = data (length-delimited bytes)
      const lenV = readVarint(buffer, offset);
      offset += lenV.bytesRead;
      data = buffer.slice(offset, offset + lenV.value);
      offset += lenV.value;
    } else if (wireType === 0) {
      // Autre varint — skip
      const v = readVarint(buffer, offset);
      offset += v.bytesRead;
    } else if (wireType === 2) {
      // Autre length-delimited — skip
      const lenV = readVarint(buffer, offset);
      offset += lenV.bytesRead;
      offset += lenV.value;
    } else if (wireType === 5) {
      offset += 4; // fixed32
    } else if (wireType === 1) {
      offset += 8; // fixed64
    } else {
      break;
    }
  }

  if (code === null) return null;
  return { code, data: data || new Uint8Array(0) };
}

/**
 * Décoder un buffer Protobuf et identifier son type
 * @param {Buffer|Uint8Array} buffer - Le buffer à décoder
 * @returns {Object|null} - { type, code, data, buffer, size, text, hex } ou null
 */
export function decodeProtobuf(buffer) {
  if (buffer.length < 2) return null;

  const pushMsg = decodePushMsgManual(buffer);

  if (pushMsg) {
    const legacyType = CODE_TO_LEGACY_TYPE[pushMsg.code] || 'unknown';
    return {
      type: legacyType,       // "0xF5" etc. pour compatibilité
      code: pushMsg.code,     // 10101 etc. (le vrai code)
      data: pushMsg.data,     // bytes internes du PushMsg
      buffer: buffer,         // buffer complet (pour les anciens parsers)
      size: buffer.length,
      text: bufferToText(buffer),
      hex: bufferToHex(buffer),
    };
  }

  return {
    type: 'unknown',
    code: null,
    data: null,
    buffer: buffer,
    size: buffer.length,
    hex: bufferToHex(buffer),
    text: bufferToText(buffer),
  };
}

/**
 * Convertir un buffer en chaîne hexadécimale
 * @param {Buffer} buffer
 * @returns {string}
 */
export function bufferToHex(buffer) {
  return Array.from(buffer)
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
}

/**
 * Convertir un buffer en texte lisible (caractères non-imprimables remplacés)
 * @param {Buffer|Uint8Array} buffer
 * @returns {string}
 */
export function bufferToText(buffer) {
  try {
    const decoder = new TextDecoder('utf-8', { fatal: false });
    const text = decoder.decode(buffer instanceof Uint8Array ? buffer : new Uint8Array(buffer));
    return text.replace(/[^\x20-\x7E]/g, '\u00B7');
  } catch {
    return '';
  }
}

/**
 * Lire un varint depuis un buffer à une position donnée
 * @param {Buffer} buffer
 * @param {number} offset
 * @returns {{ value: number, bytesRead: number }}
 */
export function readVarint(buffer, offset) {
  let value = 0;
  let shift = 0;
  let bytesRead = 0;
  let byte;

  do {
    if (offset + bytesRead >= buffer.length) break;
    byte = buffer[offset + bytesRead];
    value |= (byte & 0x7f) << shift;
    shift += 7;
    bytesRead++;
  } while ((byte & 0x80) !== 0 && shift < 32);

  return { value, bytesRead };
}

/**
 * Lire un float32 little-endian depuis un buffer
 * @param {Uint8Array|Buffer} buffer
 * @param {number} offset
 * @returns {number}
 */
export function readFloat32LE(buffer, offset) {
  const bytes = [
    buffer[offset],
    buffer[offset + 1],
    buffer[offset + 2],
    buffer[offset + 3],
  ];

  const arrayBuffer = new ArrayBuffer(4);
  const view = new DataView(arrayBuffer);
  for (let i = 0; i < 4; i++) {
    view.setUint8(i, bytes[i]);
  }
  return view.getFloat32(0, true); // little-endian
}
